















import {
    computed,
    defineComponent,
    PropType,
} from '@vue/composition-api';
import {
    useVuexStore,
    isMobileLayout,
} from '@superbet-group/web.lib.vue-utilities';
import authStore from '@store/modules/data/authentication/authenticationIndex';
import maintenanceIndex from '@store/modules/data/maintenance/maintenanceIndex';
import { getConfig } from '@superbet-group/web.lib.config';
import { getShellConfig } from '@src/config/shell/config';
import { useGetFeatureFlag } from '@src/app/shared/featureFlags/useGetFeatureFlag';
import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';
import { DepositType } from '@core/models/user/enums';
import { PE92ExperimentVariant } from '@core/models/user/flags';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { currentSite } from '@src/app/utils/domainHelpers';
import { Site } from '@superbet-group/web.types';

// A = no additional deposit form
// B (B1/B2) = additional deposit form shown, expanded on betslip in desktop, and retrected on home page in mobile
// C (C1/C2) = additional deposit form shown expanded on home page through all devices
// Design Variant 1 (B1/C1) = red-ish design
// Design Variant 2 (B2/C2) = grey-ish design

const PE92DesignVariant = {
    None: 0,
    Redish: 1,
    Greyish: 2,
} as const;

const PE92PlacementVariant = {
    None: 'A',
    HomeAndBetslip: 'B',
    HomeOnly: 'C',
} as const;

export default defineComponent({
    name: 'PE92InlineDepositForm',
    props: {
        positioning: {
            type: String as PropType<'homePage' | 'betSlip'>,
            required: true,
            validator: (value: string) => ['homePage', 'betSlip'].includes(value),
        },
    },
    setup(props) {
        const authModule = useVuexStore<typeof authStore>('data/authentication');
        const maintenanceModule = useVuexStore<typeof maintenanceIndex>('data/maintenance');
        const [
            chosenVariant,
            onChosenVariantReady,
        ] = useGetFeatureFlag<PE92ExperimentVariant>(
            AccountFlagNames.whichPE92ExperimentVariant,
            PE92ExperimentVariant.A,
        );

        const authenticatedUser = authModule.getter('user');
        const isUserAuthenticated = computed(() => authenticatedUser.value != null);

        const config = getConfig();
        const shellConfig = getShellConfig();
        const countryHasIndividualEligibility = config.countrySpecific.user.deposit.hasEligibility;
        const depositEligibility = authModule.getter('depositEligibility');
        const isEligibleForPix = computed(() => !(countryHasIndividualEligibility && depositEligibility.value != null && !depositEligibility.value.isOnlineEligible));

        const isDepositTypeAvailable = maintenanceModule.getter('isDepositTypeAvailable');

        const chosenDesignVariant = computed(() => {
            if (PE92ExperimentVariant.B1 === chosenVariant.value || PE92ExperimentVariant.C1 === chosenVariant.value) {
                return PE92DesignVariant.Redish;
            } else if (PE92ExperimentVariant.B2 === chosenVariant.value || PE92ExperimentVariant.C2 === chosenVariant.value) {
                return PE92DesignVariant.Greyish;
            } else {
                return PE92DesignVariant.None;
            }
        });

        const chosenPlacementVariant = computed(() => {
            if (PE92ExperimentVariant.B1 === chosenVariant.value || PE92ExperimentVariant.B2 === chosenVariant.value) {
                return PE92PlacementVariant.HomeAndBetslip;
            } else if (PE92ExperimentVariant.C1 === chosenVariant.value || PE92ExperimentVariant.C2 === chosenVariant.value) {
                return PE92PlacementVariant.HomeOnly;
            } else {
                return PE92PlacementVariant.None;
            }
        });

        // indicates if this component is shown at its current position (home page or betslip) in the current variant.
        // this assumes the prop 'positioning' is correctly set to either 'homePage' or 'betSlip'.
        const isFormLocatedAtCurrentPosition = computed(() => {
            if (chosenPlacementVariant.value === PE92PlacementVariant.HomeAndBetslip) {
                if (isMobileLayout()) {
                    return props.positioning === 'homePage';
                } else {
                    return props.positioning === 'betSlip';
                }
            }

            if (chosenPlacementVariant.value === PE92PlacementVariant.HomeOnly) {
                return props.positioning === 'homePage';
            }

            return false;
        });

        const isPixPaymentAvailable = computed(() => isDepositTypeAvailable.value(DepositType.OnlinePix));

        // If a player is logged in, they email verified. So no need to check that.
        const isUserFullyVerified = computed(() => (
            authenticatedUser.value != null
            && authenticatedUser.value.isVerifiedIdCard // document verification OK.
            && !authenticatedUser.value.shouldVerifySms // phone verification OK.
        ));

        const isUserInExperimentTimeWindow = computed(() => {
            if (authenticatedUser.value === null) {
                return false;
            }
            if (shellConfig.remoteConfig.experiments?.minimalRegistrationDateForDepositFormInHome === undefined) {
                return true;
            }
            return authenticatedUser.value.registrationDate.getTime() >= shellConfig.remoteConfig.experiments.minimalRegistrationDateForDepositFormInHome.getTime();
        });

        const meetsAllExperimentCriteria = computed(() => (
            isUserAuthenticated.value
            && isUserInExperimentTimeWindow.value
            && isEligibleForPix.value
            && isPixPaymentAvailable.value
            && isUserFullyVerified.value
            && currentSite !== Site.SuperSocialBr
        ));

        const isShown = computed(() => (
            meetsAllExperimentCriteria.value
            && isFormLocatedAtCurrentPosition.value
        ));

        const isDesignVariant1Shown = computed(() => isShown.value && chosenDesignVariant.value === PE92DesignVariant.Redish);
        const isDesignVariant2Shown = computed(() => isShown.value && chosenDesignVariant.value === PE92DesignVariant.Greyish);

        const isInitiallyOpened = computed(() => {
            if (chosenPlacementVariant.value === 'B') {
                // initially closed in mobile, but opened in desktop
                return !isMobileLayout();
            }

            // opened through all devices
            return true;
        });

        onChosenVariantReady((chosenVariantValue) => {
            if (props.positioning === 'homePage' && meetsAllExperimentCriteria.value) {
                // this will trigger even in the deposit form in the home page is hidden and the one in the betslip is shown.
                AnalyticsSDK.getInstance().log(
                    'Inline_Deposit_Form_Shown_In_Home',
                    {
                        variant: chosenVariantValue,
                    },
                );
            }
        });

        function handleTrigger() {
            AnalyticsSDK.getInstance().log(
                'Inline_Deposit_Action_Triggered',
                {
                    variant: chosenVariant.value,
                },
            );
        }

        return {
            isDesignVariant1Shown,
            isDesignVariant2Shown,
            isInitiallyOpened,
            handleTrigger,
        };
    },
    components: {
        PE92InlineDepositFormVariant1: () => import('@walletFederation/src/components/PE92InlineDepositFormVariant1.vue'),
        PE92InlineDepositFormVariant2: () => import('@walletFederation/src/components/PE92InlineDepositFormVariant2.vue'),
    },
});
